import React, { useMemo, useState } from "react";
import { PackageCheck, Undo2, Users } from "lucide-react";

import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";
import { describeApiError } from "../../../lib/apiError";
import StoreShell from "../components/StoreShell";
import StockActionModal, { type StockAction } from "../components/StockActionModal";
import { useStoreCustody } from "../utils/useStoreCustody";
import { fmtQty, staffName } from "../utils/stores";
import {
  useStoreGetHoldingsQuery,
  useStoreGetReturnRequestsQuery,
  useStoreConfirmReturnMutation,
  useStoreRejectReturnMutation,
} from "../api/storesApi";

type UiMsg = { type: "success" | "error"; text: string } | null;

/**
 * Staff — who is holding the Institute's stock.
 *
 * Stock issued to somebody has LEFT the stores: it is not in any store's
 * balance, it is in their hands until they sell it or hand it back. This page
 * is the answer to "who has what", and the place a custodian clears the
 * returns waiting on them.
 */
function StoreStaffInner() {
  const { data: holdings = [], isLoading, error } = useStoreGetHoldingsQuery();
  const { data: pending = [] } = useStoreGetReturnRequestsQuery({ status: "pending" });
  const [confirmReturn, { isLoading: confirming }] = useStoreConfirmReturnMutation();
  const [rejectReturn, { isLoading: rejecting }] = useStoreRejectReturnMutation();
  const { canWork } = useStoreCustody();

  const [msg, setMsg] = useState<UiMsg>(null);
  const [action, setAction] = useState<StockAction | null>(null);

  const flash = (m: UiMsg) => {
    setMsg(m);
    if (m) setTimeout(() => setMsg(null), 6000);
  };

  /** Grouped by person: a holder with six lines is one row to read, not six. */
  const byHolder = useMemo(() => {
    const map = new Map<string, { admin: any; lines: any[]; units: number }>();

    (holdings as any[]).forEach((h) => {
      const key = String(h.admin_id);
      const entry = map.get(key) ?? { admin: h.admin, lines: [], units: 0 };
      entry.lines.push(h);
      entry.units += Number(h.quantity ?? 0);
      map.set(key, entry);
    });

    return Array.from(map.values()).sort((a, b) => b.units - a.units);
  }, [holdings]);

  const mine = (pending as any[]).filter((r) => canWork(r.store));

  return (
    <StoreShell
      title="Staff"
      subtitle="Who is holding stock that has left the stores, and what is waiting to come back."
      actions={
        <AccessGate perm={PERMS.store.movement}>
          <button
            onClick={() => setAction("issue")}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-black text-white text-sm font-medium rounded-lg"
          >
            <PackageCheck className="w-4 h-4" /> Issue to staff
          </button>
        </AccessGate>
      }
    >
      {msg && (
        <div
          className={`rounded-xl border p-4 text-sm font-medium ${
            msg.type === "success"
              ? "bg-green-50 border-green-100 text-green-800"
              : "bg-red-50 border-red-100 text-red-800"
          }`}
        >
          {msg.text}
        </div>
      )}

      {/* The custodian's inbox. Nothing has moved while one of these is open. */}
      {mine.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Undo2 className="w-4 h-4 text-amber-600" />
            <h2 className="font-bold text-gray-800">Returns waiting on you</h2>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            They are still recorded as holding this stock until you confirm you have it back.
          </p>

          <div className="space-y-2">
            {mine.map((r: any) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {staffName(r.admin)} → {r.store?.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {fmtQty(r.quantity)} {r.item?.unit} of {r.item?.name}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={async () => {
                      try {
                        const res: any = await confirmReturn({ id: r.id }).unwrap();
                        flash({ type: "success", text: res?.message || "Confirmed." });
                      } catch (err) {
                        flash({ type: "error", text: describeApiError(err, "Could not confirm that return.") });
                      }
                    }}
                    disabled={confirming}
                    className="px-3 py-1.5 rounded-lg bg-gray-900 hover:bg-black text-white text-xs font-medium disabled:opacity-40"
                  >
                    Confirm received
                  </button>
                  <button
                    onClick={async () => {
                      const why = window.prompt("Say why it is not being accepted (optional):") ?? undefined;
                      try {
                        const res: any = await rejectReturn({ id: r.id, decision_note: why }).unwrap();
                        flash({ type: "success", text: res?.message || "Marked as not received." });
                      } catch (err) {
                        flash({ type: "error", text: describeApiError(err, "Could not update that return.") });
                      }
                    }}
                    disabled={rejecting}
                    className="px-3 py-1.5 rounded-lg border text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                  >
                    Not received
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-sm font-medium text-red-700">
          {describeApiError(error, "Could not load the holdings.")}
        </div>
      )}

      {isLoading && <div className="py-14 text-center text-sm text-gray-400 animate-pulse">Loading…</div>}

      {!isLoading && byHolder.length === 0 && (
        <div className="bg-white border rounded-xl py-16 text-center px-6">
          <Users className="w-9 h-9 text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-500">Nobody is holding stock</p>
          <p className="text-xs text-gray-400 mt-1">
            Issue stock to a member of staff and it appears here until they sell or return it.
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {byHolder.map(({ admin, lines, units }) => (
          <div key={admin?.id ?? Math.random()} className="bg-white border rounded-xl p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-bold text-gray-900">{staffName(admin)}</div>
                <div className="text-xs text-gray-400">
                  {lines.length} item line{lines.length === 1 ? "" : "s"} · {fmtQty(units)} units
                </div>
              </div>
              <AccessGate perm={PERMS.store.movement}>
                <button
                  onClick={() => setAction("sale")}
                  className="px-3 py-1.5 rounded-lg border text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  Record sale
                </button>
              </AccessGate>
            </div>

            <div className="divide-y divide-gray-50 mt-3">
              {lines.map((l: any) => (
                <div key={l.id} className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm text-gray-800 truncate">{l.item?.name}</div>
                    <div className="text-xs text-gray-400 font-mono">{l.item?.code}</div>
                  </div>
                  <div className="text-sm font-medium text-gray-900 shrink-0">
                    {fmtQty(l.quantity)} <span className="text-gray-400 text-xs">{l.item?.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {action && <StockActionModal action={action} onClose={() => setAction(null)} />}
    </StoreShell>
  );
}

export default function StoreStaffPage() {
  return (
    <PageGate perm={PERMS.store.access}>
      <StoreStaffInner />
    </PageGate>
  );
}
